/**
 * The POS store.
 *
 * Four reducers, and only one of them holds server state — RTK Query's. The other three are
 * genuine client state: who is signed in, what is in the cart, which theme. Nothing fetched ever
 * lands in a slice.
 */

import { configureStore } from '@reduxjs/toolkit';

import {
  api,
  configureApi,
  expiredLocally,
  readHint,
  resetApiState,
  restored,
  revoked,
  selectActiveShopId,
  selectUser,
  sessionReducer,
  themeReducer,
  wipeLocalState,
} from '@pos/shared';

import { cartReducer, saveCart } from '@/features/sale-checkout';
import { env } from '@/shared/config/env';

export const store = configureStore({
  reducer: {
    session: sessionReducer,
    cart: cartReducer,
    theme: themeReducer,
    [api.reducerPath]: api.reducer,
  },
  middleware: (getDefault) => getDefault().concat(api.middleware),
  devTools: !env.isProduction,
});

/**
 * Wired after the store exists, because the handler dispatches into it.
 *
 * `DEVICE_REVOKED` means another device signed in as this user, or the owner revoked this one.
 * Everything local goes — including the cached catalogue, which carries per-shop stock and
 * prices and belongs to a session that no longer exists.
 */
configureApi({
  baseUrl: env.apiBaseUrl,
  channel: 'pos',
  // The server already says exactly what happened (revoked by an owner, another device signed
  // in, the PIN limit was hit, or the session simply expired) without pretending to know which
  // one it was when it does not — a frontend-invented guess like "someone signed in elsewhere"
  // would be a specific, possibly false claim standing in for an honest, general one.
  onSessionEnded: (_code, message) => {
    store.dispatch(revoked(message));

    store.dispatch(resetApiState());
    void wipeLocalState();
  },
  // The access token expired while this tab was away — a stale tab woken up, not a revocation.
  // Nothing here is wiped: the PIN screen is the till's genuine recovery, not a click alone (that
  // is exactly what a PIN protects against on an unattended device). See `recoverSession` in
  // `baseQuery.ts` and its `expiredLocally` reducer.
  onNeedsUnlock: () => {
    store.dispatch(expiredLocally());
  },
});

/**
 * Boot resolves synchronously, here, rather than in an effect on the first screen.
 *
 * There is nothing to await: the hint is a `localStorage` read, and the refresh cookie is
 * something only the server can confirm. Doing it at store creation means the router's first
 * render already knows whether to show the keypad or the password form, so no guard ever paints
 * the wrong screen for a frame.
 */
// The cart is written to the device whenever it changes, so a reload or a discarded tab does not
// lose a sale in progress. Only while someone is signed in: after a sign-out the wipe clears it,
// and nothing here writes it back. See `cartStorage`.
let lastCart = store.getState().cart;
store.subscribe(() => {
  const state = store.getState();
  if (state.cart === lastCart) return;
  lastCart = state.cart;

  const userId = selectUser(state)?.id;
  const shopId = selectActiveShopId(state);
  if (userId !== undefined && shopId !== null) saveCart({ userId, shopId }, state.cart);
});

const hint = readHint();
store.dispatch(restored({ status: hint?.hasPin === true ? 'locked' : 'anonymous' }));

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
